import React from 'react';
import styled from '@emotion/styled';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { BASE, SERIES_COLOURS } from './theme';

/**
 * One table's slice as a chart (statistics_view.v1). `slice` is sliceValues()'s
 * result: one panel is one chart, several panels a small-multiples grid.
 * Colours follow the series in fixed order; a legend shows for two or more
 * series; a missing value breaks the line (never drawn as zero).
 */

const Grid = styled.div`
  flex: 1; min-height: 0; display: grid; gap: 1.5vh 1.5vw;
  grid-template-columns: repeat(${(p) => p.$cols}, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
`;
const Panel = styled.div`
  min-height: ${(p) => (p.$wall ? '0' : '16rem')}; display: flex; flex-direction: column; gap: 0.6vh;
  border-radius: 14px; padding: ${(p) => (p.$wall ? '1.4vh 1vw' : '12px')}; background: ${BASE.panel}; border: 1px solid ${BASE.line};
`;
const PanelTitle = styled.h3`margin: 0; font-size: ${(p) => (p.$wall ? 'clamp(1rem, 1.6vw, 1.9rem)' : '1rem')}; font-weight: 600;`;
const ChartBox = styled.div`flex: 1; min-height: ${(p) => (p.$wall ? '0' : '14rem')};`;

const number = (v) => (v == null ? '' : new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 2 }).format(v));

function rowsFor(panel) {
  return panel.xs.map((x, i) => {
    const row = { x };
    for (const s of panel.series) row[s.key] = s.points[i]?.value ?? null;
    return row;
  });
}

export default function SeriesChart({ slice, chart = 'line', unit = null, wall = false, missingLabel = '' }) {
  const panels = slice?.panels || [];
  if (!panels.length) return null;
  const cols = panels.length <= 1 ? 1 : panels.length <= 4 ? 2 : 3;
  const fontSize = wall ? 18 : 12;
  const axis = { stroke: BASE.muted, tick: { fill: BASE.muted, fontSize }, tickLine: false, axisLine: { stroke: BASE.line } };
  const tooltip = {
    contentStyle: { background: BASE.panel, border: `1px solid ${BASE.line}`, borderRadius: 10, color: BASE.text, fontSize },
    formatter: (v) => (v == null ? missingLabel : `${number(v)}${unit ? ` ${unit}` : ''}`),
  };
  return (
    <Grid $cols={cols}>
      {panels.map((panel) => {
        const data = rowsFor(panel);
        const showLegend = panel.series.length >= 2;
        const Chart = chart === 'bars' ? BarChart : LineChart;
        return (
          <Panel key={panel.key} $wall={wall}>
            {panel.label && <PanelTitle $wall={wall}>{panel.label}</PanelTitle>}
            <ChartBox $wall={wall}>
              <ResponsiveContainer width="100%" height="100%">
                <Chart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }} barGap={2} barCategoryGap="20%">
                  <CartesianGrid stroke={BASE.grid} vertical={false} />
                  <XAxis dataKey="x" {...axis} />
                  <YAxis {...axis} tickFormatter={number} width={wall ? 90 : 60} />
                  <Tooltip {...tooltip} cursor={{ stroke: BASE.muted, fill: 'rgba(255,255,255,0.05)' }} />
                  {showLegend && <Legend wrapperStyle={{ color: BASE.text, fontSize }} iconType="plainline" />}
                  {panel.series.map((s, i) => (chart === 'bars'
                    ? <Bar key={s.key} dataKey={s.key} name={s.label} fill={SERIES_COLOURS[i % SERIES_COLOURS.length]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                    : <Line key={s.key} dataKey={s.key} name={s.label} stroke={SERIES_COLOURS[i % SERIES_COLOURS.length]} strokeWidth={wall ? 3 : 2}
                      dot={{ r: wall ? 5 : 3 }} connectNulls={false} isAnimationActive={false} />
                  ))}
                </Chart>
              </ResponsiveContainer>
            </ChartBox>
          </Panel>
        );
      })}
    </Grid>
  );
}
